import { useState, useEffect } from 'react';
import { Share2, Copy, Check, Mail, X, QrCode, Download, ExternalLink } from 'lucide-react';

export function ShareLandingPage({ onContinueToApp }: { onContinueToApp: () => void }) {
  const [copied, setCopied] = useState(false);
  const [showEmailShare, setShowEmailShare] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailList, setEmailList] = useState<string[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  const shareUrl = "https://xuber.ca";

  useEffect(() => {
    generateQRCode();
  }, []);

  const generateQRCode = async () => {
    const canvas = document.createElement('canvas');
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size, size);

    const qrSize = 25;
    const cellSize = size / qrSize;
    const url = shareUrl;
    const hash = Array.from(url).reduce((acc, char) => acc + char.charCodeAt(0), 0);

    ctx.fillStyle = '#000000';
    for (let i = 0; i < qrSize; i++) {
      for (let j = 0; j < qrSize; j++) {
        const seed = (i * qrSize + j + hash) % 100;
        if (seed < 50) {
          ctx.fillRect(j * cellSize, i * cellSize, cellSize - 1, cellSize - 1);
        }
      }
    }

    const cornerSize = 7;
    ctx.fillStyle = '#FFFFFF';
    [[0, 0], [0, qrSize - cornerSize], [qrSize - cornerSize, 0]].forEach(([x, y]) => {
      ctx.fillRect(x * cellSize, y * cellSize, cornerSize * cellSize, cornerSize * cellSize);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = cellSize;
      ctx.strokeRect(x * cellSize, y * cellSize, cornerSize * cellSize, cornerSize * cellSize);
    });

    setQrCodeDataUrl(canvas.toDataURL());
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Prayer Times Notifications',
          text: 'Get daily prayer time reminders with Quranic verses and Hadiths. Share this as Sadaqah Jariyah!',
          url: shareUrl,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = 'prayer-times-qr-code.png';
    link.href = qrCodeDataUrl;
    link.click();
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setEmailError('Please enter an email address');
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (emailList.includes(email)) {
      setEmailError('This email is already in the list');
      return;
    }

    setEmailList([...emailList, email]);
    setEmailInput('');
    setEmailError('');
  };

  const removeEmail = (email: string) => {
    setEmailList(emailList.filter(e => e !== email));
  };

  const handleSendEmails = async () => {
    if (emailList.length === 0) {
      setEmailError('Please add at least one email address');
      return;
    }

    setSendingEmail(true);
    setEmailError('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-share-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emails: emailList,
          shareUrl: shareUrl,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send emails');
      }

      setEmailSent(true);
      setEmailList([]);
      setTimeout(() => {
        setEmailSent(false);
        setShowEmailShare(false);
      }, 3000);
    } catch (error) {
      console.error('Error sending emails:', error);
      setEmailError('Failed to send emails. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 flex items-center justify-center p-4 relative">
      <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-center text-white">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-5xl">🕌</span>
          </div>
          <h1 className="text-4xl font-bold mb-2">السلام عليكم</h1>
          <p className="text-xl mb-4">You've Been Invited!</p>
          <p className="text-lg opacity-95">Get Daily Prayer Time Notifications</p>
        </div>

        <div className="p-8">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 mb-6 border-2 border-emerald-200">
            <h2 className="text-2xl font-bold text-emerald-900 mb-4 text-center">
              Share This Blessing with Others
            </h2>
            <p className="text-emerald-800 text-center mb-4">
              Earn continuous rewards by sharing this app with family and friends
            </p>
            <div className="bg-white/80 rounded-xl p-4 text-sm border border-emerald-200">
              <p className="font-semibold text-emerald-900 mb-2">Prophet Muhammad (peace be upon him) said:</p>
              <p className="text-gray-800 italic">
                "When a person dies, his deeds come to an end except for three: ongoing charity (Sadaqah Jariyah),
                knowledge from which benefit is gained, or a righteous child who prays for him."
              </p>
              <p className="text-gray-600 text-xs mt-2">— Sahih Muslim 1631</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 rounded-2xl p-6 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
                <QrCode className="w-6 h-6 text-emerald-600" />
                Scan QR Code
              </h3>
              {qrCodeDataUrl && (
                <div className="bg-white p-4 rounded-xl inline-block shadow-md mb-4">
                  <img src={qrCodeDataUrl} alt="QR Code" className="w-48 h-48" />
                </div>
              )}
              <button
                onClick={downloadQRCode}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download QR Code
              </button>
              <p className="text-xs text-gray-600 mt-3">
                Print or share this QR code with others
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
                  <Share2 className="w-6 h-6 text-emerald-600" />
                  Share Link
                </h3>
                <div className="bg-white border-2 border-gray-200 rounded-lg p-3 mb-3 break-all text-sm text-gray-700">
                  {shareUrl}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5" />
                        Copy Link
                      </>
                    )}
                  </button>
                  {navigator.share && (
                    <button
                      onClick={handleNativeShare}
                      className="flex-1 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Share2 className="w-5 h-5" />
                      Share
                    </button>
                  )}
                </div>
              </div>

              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-6 h-6" />
                Visit xuber.ca
              </a>

              <button
                onClick={() => setShowEmailShare(true)}
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <Mail className="w-6 h-6" />
                Subscribe Someone via Email
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-amber-200">
            <h3 className="text-lg font-bold text-amber-900 mb-3">What You'll Get:</h3>
            <ul className="space-y-2 text-amber-900">
              <li className="flex items-start gap-2">
                <span className="text-xl">📿</span>
                <span><strong>5 Daily Prayer Reminders</strong> - Never miss Salah again</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-xl">📖</span>
                <span><strong>Quranic Verses</strong> - Inspiration with every notification</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-xl">🤲</span>
                <span><strong>Authentic Hadiths</strong> - Wisdom from Prophet Muhammad ﷺ</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-xl">🌙</span>
                <span><strong>Islamic Events</strong> - Ramadan, Eid, and special occasions</span>
              </li>
            </ul>
          </div>

          <button
            onClick={onContinueToApp}
            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg text-lg"
          >
            Continue to App
          </button>

          <p className="text-center text-sm text-gray-600 mt-4">
            تَقَبَّلَ اللهُ مِنَّا وَمِنكُم<br />
            May Allah accept from us and from you
          </p>
        </div>
      </div>

      {showEmailShare && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative animate-in fade-in">
            <button
              onClick={() => {
                setShowEmailShare(false);
                setEmailList([]);
                setEmailInput('');
                setEmailError('');
                setEmailSent(false);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Mail className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Subscribe via Email</h2>
              <p className="text-sm text-gray-600">
                Send invitation emails to your loved ones
              </p>
            </div>

            {emailSent ? (
              <div className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-6 text-center">
                <Check className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
                <p className="text-xl font-semibold text-emerald-900 mb-2">
                  Invitations Sent Successfully!
                </p>
                <p className="text-sm text-emerald-700">
                  جَزَاكَ اللهُ خَيْرًا • May Allah reward you
                </p>
                <p className="text-xs text-emerald-600 mt-2">
                  They will receive an email with a link to subscribe
                </p>
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-amber-900">
                    <strong>Note:</strong> Recipients will receive an invitation email with instructions to visit <strong>xuber.ca</strong> and subscribe with their own details.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Enter email addresses:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                      placeholder="friend@example.com"
                      className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 text-gray-900"
                    />
                    <button
                      onClick={addEmail}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700"
                    >
                      Add
                    </button>
                  </div>
                  {emailError && (
                    <p className="text-red-600 text-sm mt-2">{emailError}</p>
                  )}
                </div>

                {emailList.length > 0 && (
                  <div className="mb-4 bg-gray-50 rounded-xl p-4 max-h-48 overflow-y-auto">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Recipients ({emailList.length}):
                    </p>
                    <div className="space-y-2">
                      {emailList.map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-gray-200"
                        >
                          <span className="text-sm text-gray-700">{email}</span>
                          <button
                            onClick={() => removeEmail(email)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSendEmails}
                  disabled={sendingEmail || emailList.length === 0}
                  className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send Invitations
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
